/**
 * EnergyActionTabs.tsx
 *
 * Tab strip for filtering the Energieprestaties table by action state.
 *
 * Tabs:
 *   - "Acties Nodig" (default) — epcRefreshSuggested === true
 *   - "Werken Uitgevoerd"      — projectPartGroups.length > 0
 *   - "Geen EPC"               — currentEPC === null
 *   - "Alle"                   — no filter
 *
 * Selection is persisted per session in localStorage.
 */
import { useMemo } from "react";
import { EPCData } from "../hooks/useEnergyPerformanceData";

export type ActionTab = "acties" | "werken" | "geen-epc" | "alle";

const TAB_STORAGE_KEY = "energy-action-tab";

const TAB_LABELS: Record<ActionTab, string> = {
  acties: "Acties Nodig",
  werken: "Werken Uitgevoerd",
  "geen-epc": "Geen EPC",
  alle: "Alle",
};

interface EnergyActionTabsProps {
  data: EPCData[];
  activeTab: ActionTab;
  onTabChange: (tab: ActionTab) => void;
}

export function getStoredActionTab(): ActionTab {
  try {
    const stored = sessionStorage.getItem(TAB_STORAGE_KEY) as ActionTab | null;
    if (stored && (stored === "acties" || stored === "werken" || stored === "geen-epc" || stored === "alle")) {
      return stored;
    }
  } catch {
    // sessionStorage unavailable — use default
  }
  return "alle";
}

export function storeActionTab(tab: ActionTab): void {
  try {
    sessionStorage.setItem(TAB_STORAGE_KEY, tab);
  } catch {
    // sessionStorage unavailable — ignore
  }
}

/** Returns the subset of data matching the given tab. */
export function filterByActionTab(data: EPCData[], tab: ActionTab): EPCData[] {
  switch (tab) {
    case "acties":
      return data.filter((u) => u.epcRefreshSuggested);
    case "werken":
      return data.filter((u) => (u.projectPartGroups?.length ?? 0) > 0);
    case "geen-epc":
      return data.filter((u) => !u.currentEPC);
    case "alle":
    default:
      return data;
  }
}

export function EnergyActionTabs({
  data,
  activeTab,
  onTabChange,
}: EnergyActionTabsProps) {
  const counts = useMemo<Record<ActionTab, number>>(
    () => ({
      acties: data.filter((u) => u.epcRefreshSuggested).length,
      werken: data.filter((u) => (u.projectPartGroups?.length ?? 0) > 0).length,
      "geen-epc": data.filter((u) => !u.currentEPC).length,
      alle: data.length,
    }),
    [data],
  );

  const tabs: ActionTab[] = ["acties", "werken", "geen-epc", "alle"];

  return (
    <div className="mb-4 flex gap-1 overflow-x-auto border-b border-neutral-primary/10">
      {tabs.map((tab) => {
        const isActive = activeTab === tab;
        return (
          <button
            key={tab}
            type="button"
            onClick={() => {
              onTabChange(tab);
              storeActionTab(tab);
            }}
            className={`flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              isActive
                ? "border-moss-primary text-moss-primary"
                : "border-transparent text-neutral-secondary-mid hover:text-neutral-primary hover:border-neutral-primary/30"
            }`}
          >
            {TAB_LABELS[tab]}
            <span
              className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                isActive
                  ? "bg-moss-primary text-white"
                  : "bg-neutral-primary/10 text-neutral-secondary-mid"
              }`}
            >
              {counts[tab]}
            </span>
          </button>
        );
      })}
    </div>
  );
}
